import { useTranslations } from '../../../contexts/TranslationContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { resolveCtaTargetCard, formatCtaTargetCardText } from '../../../utils/ctaHelpers';

/**
 * Resolve game-type-aware copy for the draws page.
 *
 * The `draws.howItWorksByGame` block in translations.json holds per-game-type
 * overrides for the how-it-works title/body/bullets. Anything missing falls
 * back to the matching key under `draws.howItWorks.*` (which itself is now
 * game-neutral so an unmapped game type still reads correctly).
 *
 * Also exposes `prizeLabelKey` — the upcoming-grid label to use for the per-draw
 * dollar amount. Prize draws expose a fixed prize value rather than a building
 * jackpot, so we render "Prize Value" instead of "Current Jackpot" for them.
 *
 * @returns {{ t: Function, howItWorks: (field:string)=>string, isPrizeGame: boolean, prizeLabel: string, currentJackpotLabel: string, estimatedValueLabel: string, prizeTBDLabel: string }}
 */
export function useDrawsCopy() {
  const { t } = useTranslations();
  const { gameType } = useGameType();
  const { siteDetails } = useSiteConfig() || {};
  const { language } = useLanguage();

  const isPrizeGame = gameType === 'prizeDraw';
  const ctaTarget = resolveCtaTargetCard([], siteDetails);

  const howItWorks = (field) => {
    let copy = '';
    if (gameType) {
      const byGame = t(`draws.howItWorksByGame.${gameType}.${field}`);
      // The translator returns the key path itself when nothing is found, so
      // detect that and fall back to the game-neutral default.
      if (byGame && !byGame.startsWith('draws.howItWorksByGame.')) {
        copy = byGame;
      }
    }
    if (!copy) {
      copy = t(`draws.howItWorks.${field}`);
    }
    if (copy && typeof copy === 'string') {
      copy = formatCtaTargetCardText(copy, ctaTarget, '', language);
    }
    return copy;
  };

  // Label for the upcoming-draw amount/price box.
  const currentJackpotLabel = t('draws.upcoming.currentJackpot');
  const weeklyPotLabel = t('draws.upcoming.weeklyPot', t('chaseTheAce.weeklyPrize', 'Weekly Pot'));
  const prizeLabel = t('draws.upcoming.prizeLabel', currentJackpotLabel);
  const prizeLabelUsed = isPrizeGame ? prizeLabel : currentJackpotLabel;

  // For prize draws, the prize name is the headline; the dollar amount becomes
  // a secondary "Est. Value" line. When no prize name is configured on a draw,
  // we render "Prize TBA" rather than a misleading "$0".
  const estimatedValueLabel = t('draws.upcoming.estimatedValue', 'Est. Value');
  const prizeTBDLabel = t('draws.upcoming.prizeTBD', 'Prize TBA');

  return {
    t,
    howItWorks,
    isPrizeGame,
    prizeLabel: prizeLabelUsed,
    currentJackpotLabel,
    weeklyPotLabel,
    estimatedValueLabel,
    prizeTBDLabel,
  };
}
